import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import {
    ADMIN_ROLE,
    ChangePasswordRequest,
    LoginRequest,
    LoginResponse,
    SUPER_ADMIN_ROLE,
    User,
} from 'app/core/user/user.types';
import { environment } from 'environments/environment';
import { catchError, Observable, of, switchMap, tap, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AuthService {
    private _authenticated: boolean = false;
    private _httpClient = inject(HttpClient);
    private _userService = inject(UserService);

    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        return localStorage.getItem('accessToken') ?? '';
    }

    forgotPassword(email: string): Observable<any> {
        return this._httpClient.post(
            `${environment.apiBaseUrl}/auth/forgot-password`,
            { email }
        );
    }

    resetPassword(password: string): Observable<any> {
        return this._httpClient.post(
            `${environment.apiBaseUrl}/auth/reset-password`,
            { password }
        );
    }

    changePassword(request: ChangePasswordRequest): Observable<any> {
        return this._httpClient.post(
            `${environment.apiBaseUrl}/auth/change-password`,
            request
        );
    }

    signIn(credentials: LoginRequest): Observable<LoginResponse> {
        if (this._authenticated) {
            return throwError(() => new Error('User is already logged in.'));
        }

        return this._httpClient
            .post<LoginResponse>(
                `${environment.apiBaseUrl}/auth/login`,
                credentials
            )
            .pipe(
                switchMap((response: LoginResponse) => {
                    // Allow Admin (society admin) and SuperAdmin (platform) roles.
                    if (
                        response.roleName !== ADMIN_ROLE &&
                        response.roleName !== SUPER_ADMIN_ROLE
                    ) {
                        return throwError(
                            () =>
                                new Error(
                                    'Access denied. Only Admin users can access this panel.'
                                )
                        );
                    }

                    this.accessToken = response.token;
                    this._authenticated = true;

                    // Store user data
                    const user: User = {
                        id: response.userId,
                        name: response.fullName,
                        email: response.email,
                        societyId: response.societyId,
                        societyName: response.societyName,
                        roleName: response.roleName,
                        flatId: response.flatId,
                        flatNumber: response.flatNumber,
                        flatOwnerId: response.flatOwnerId,
                        wingId: response.wingId,
                        wingName: response.wingName,
                        canManageCommonAccount:
                            response.canManageCommonAccount ?? false,
                    };
                    this._userService.user = user;

                    // Persist user data for page refresh
                    localStorage.setItem('userData', JSON.stringify(user));

                    return of(response);
                })
            );
    }

    signInUsingToken(): Observable<boolean> {
        // Validate token is not expired
        if (!this.accessToken || AuthUtils.isTokenExpired(this.accessToken)) {
            return of(false);
        }

        // Restore user from localStorage
        const storedUser = localStorage.getItem('userData');
        if (!storedUser) {
            return of(false);
        }

        try {
            const user: User = JSON.parse(storedUser);
            // Allow Admin and SuperAdmin roles.
            if (
                user.roleName !== ADMIN_ROLE &&
                user.roleName !== SUPER_ADMIN_ROLE
            ) {
                this.signOut();
                return of(false);
            }
            this._authenticated = true;
            this._userService.user = user;
            return of(true);
        } catch {
            return of(false);
        }
    }

    signOut(): Observable<any> {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('userData');
        // Clear any SuperAdmin impersonation backup so a fresh login starts clean.
        localStorage.removeItem('superAdminToken');
        localStorage.removeItem('superAdminUserData');
        this._authenticated = false;
        return of(true);
    }

    check(): Observable<boolean> {
        if (this._authenticated) {
            return of(true);
        }

        if (!this.accessToken) {
            return of(false);
        }

        if (AuthUtils.isTokenExpired(this.accessToken)) {
            this.signOut();
            return of(false);
        }

        return this.signInUsingToken();
    }
}
